import { ChartTypeRegistry } from 'chart.js';

import { REPORT_TEMPLATES_COLUMNS } from 'interfaces/report-templates.interface';
import { OptionType, TableColumn } from 'interfaces/shared.interface';

import { BASE_FILTER_FIELDS } from './common';

export const REPORT_TEMPLATES_COLUMNS_TITLES = {
  [REPORT_TEMPLATES_COLUMNS.NAME]: 'Name',
  [REPORT_TEMPLATES_COLUMNS.TYPE]: 'Type',
  [REPORT_TEMPLATES_COLUMNS.CREATOR]: 'Creator',
  [REPORT_TEMPLATES_COLUMNS.CREATION_TIME]: 'CreationTime',
  [REPORT_TEMPLATES_COLUMNS.ACTIONS]: '',
};

export const REPORT_TEMPLATES_TABLE_COLUMNS: TableColumn[] = [
  {
    columnId: REPORT_TEMPLATES_COLUMNS.NAME,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.NAME],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.TYPE,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.TYPE],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.CREATOR,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.CREATOR],
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.CREATION_TIME,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.CREATION_TIME],
    sorting: true,
  },
  {
    columnId: REPORT_TEMPLATES_COLUMNS.ACTIONS,
    title: REPORT_TEMPLATES_COLUMNS_TITLES[REPORT_TEMPLATES_COLUMNS.ACTIONS],
  },
];

export const TEMPLATES_DEFAULT_FILTERS_VALUES = {
  [BASE_FILTER_FIELDS.SEARCH_FIELD]: '',
};

export type ReportTypeOption = {
  label: string;
  value: number;
};

export const REPORT_TYPES = [
  { value: 1, label: 'Excel' },
  { value: 2, label: 'CSV' },
];

export const FILTER_CONDITIONS_FOR_NUMBERS = [
  { id: 1, label: 'Is' },
  { id: 2, label: 'Is not' },
  { id: 3, label: 'Greater than' },
  { id: 4, label: 'Less than' },
  { id: 5, label: 'Greater than or equal to' },
  { id: 6, label: 'Less than or equal to' },
  { id: 7, label: 'Is null' },
  { id: 8, label: 'Is not null' },
];

export const FILTER_CONDITIONS_FOR_STRINGS = [
  { id: 1, label: 'Is' },
  { id: 2, label: 'Is not' },
  { id: 3, label: 'Contains' },
  { id: 4, label: 'Does not contain' },
  { id: 5, label: 'Starts with' },
  { id: 6, label: 'Ends with' },
  { id: 7, label: 'Does not start with' },
  { id: 8, label: 'Does not end with' },
  { id: 9, label: 'Is null' },
  { id: 10, label: 'Is not null' },
];

export const FILTER_CONDITIONS_FOR_BOOLEANS = [
  { id: 1, label: 'Is' },
  { id: 2, label: 'Is not' },
  { id: 3, label: 'Is null' },
  { id: 4, label: 'Is not null' },
];

export enum ReportFilterSectionId {
  JOIN = 'join',
  GROUPING = 'grouping',
  AGGREGATE = 'aggregate',
  FILTER = 'filter',
  SORT = 'sort',
}

export const REPORT_FILTER_SECTIONS = [
  { id: ReportFilterSectionId.JOIN, label: 'AddJoin' },
  { id: ReportFilterSectionId.GROUPING, label: 'Grouping' },
  { id: ReportFilterSectionId.AGGREGATE, label: 'AggregateFunctions' },
  { id: ReportFilterSectionId.FILTER, label: 'Filters' },
  { id: ReportFilterSectionId.SORT, label: 'SortBy' },
];

export type AggregateFunctionType =
  | 'Count'
  | 'Distinct count of'
  | 'Sum of'
  | 'Average of'
  | 'Maximum of'
  | 'Minimum of';

export const AGGREGATE_OPTIONS: AggregateFunctionType[] = [
  'Count',
  'Distinct count of',
  'Sum of',
  'Average of',
  'Maximum of',
  'Minimum of',
];

export const AGGREGATE_FUNCTION_TYPE_MAP: Record<AggregateFunctionType, number> = {
  Count: 1,
  'Distinct count of': 2,
  'Sum of': 3,
  'Average of': 4,
  'Maximum of': 5,
  'Minimum of': 6,
};

export const AGGREGATE_FUNCTION_NUMBER_MAP: Record<number, AggregateFunctionType> = {
  1: 'Count',
  2: 'Distinct count of',
  3: 'Sum of',
  4: 'Average of',
  5: 'Maximum of',
  6: 'Minimum of',
};

export const getAggregateFunctionTypeString = (numOrString: number | string | null): AggregateFunctionType => {
  if (typeof numOrString === 'string') {
    return numOrString as AggregateFunctionType;
  }
  if (typeof numOrString === 'number') {
    return AGGREGATE_FUNCTION_NUMBER_MAP[numOrString] || 'Sum of';
  }
  return 'Sum of';
};

export const getAggregateFunctionNumber = (func: unknown): number => {
  if (typeof func === 'string' && func in AGGREGATE_FUNCTION_TYPE_MAP) {
    return AGGREGATE_FUNCTION_TYPE_MAP[func as AggregateFunctionType];
  }
  return 3;
};

export type AggregateItem = {
  id: number;
  func: AggregateFunctionType | null;
  column?: string | null;
};

export type FilterItem = {
  id: string;
  column: string | null;
  condition: number | null;
  value: any;
  locked: boolean;
  operator?: 'And' | 'Or';
};

export const FILTER_CONDITIONS_FOR_NEW_FILTER = [
  { label: 'And', value: 'And' },
  { label: 'Or', value: 'Or' },
];

export type SortDirection = 'asc' | 'desc';

export const SORT_DIRECTIONS = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
];

export type VisualizationType = 'Table' | 'Bar' | 'Line' | 'Pie' | 'Doughnut' | 'Scatter' | 'Polar Area' | 'Radar';

export const VISUALIZATION_OPTIONS = [
  { label: 'Table', value: 'Table' },
  { label: 'Bar', value: 'Bar' },
  { label: 'Line', value: 'Line' },
  { label: 'Pie', value: 'Pie' },
  { label: 'Doughnut', value: 'Doughnut' },
  { label: 'Scatter', value: 'Scatter' },
  { label: 'Polar Area', value: 'Polar Area' },
  { label: 'Radar', value: 'Radar' },
];

export type VisualizationFormState =
  | {
      type: 'Table';
      columns: string[];
      rowLimit?: number;
    }
  | {
      type: Exclude<VisualizationType, 'Table'>;
      column: string;
      timelapseType?: number;
    };

export const getTypeFromDataType = (dataType: string): number => {
  const map: Record<string, number> = {
    INT: 3,
    VARCHAR: 2,
    BOOL: 1,
    DATE: 4,
    DATETIME: 5,
  };
  return map[dataType.toUpperCase()];
};

export const getDisplayTypeFromDataType = (dataType: string): string => {
  const map: Record<string, string> = {
    INT: 'INT',
    VARCHAR: 'VARCHAR',
    BOOL: 'BOOLEAN',
    DATE: 'DATE',
    DATETIME: 'DATETIME',
  };
  return map[dataType.toUpperCase()];
};

export const getTimelapseTypeFromDataType = (dataType: string): number => {
  const map: Record<string, number> = {
    DATE: 1,
    DATETIME: 1,
  };
  return map[dataType.toUpperCase()];
};

export const chartTypeMap: Record<string, keyof ChartTypeRegistry> = {
  Bar: 'bar',
  Line: 'line',
  Pie: 'pie',
  Doughnut: 'doughnut',
  Scatter: 'scatter',
  'Polar Area': 'polarArea',
  Radar: 'radar',
};

export const FILTER_TIMELAPSE_TIME: OptionType[] = [
  { value: 1, label: 'Yearly' },
  { value: 2, label: 'Monthly' },
  { value: 3, label: 'Weekly' },
  { value: 4, label: 'Daily' },
];

export const typeStringMap: Record<number, string> = {
  1: 'BOOL',
  2: 'VARCHAR',
  3: 'INT',
  4: 'DATE',
  5: 'DATETIME',
};

export type TableRow = Record<string, string | number | Date | null>;

export const visualizationTypeMap: Record<VisualizationType, number> = {
  Table: 1,
  Bar: 2,
  Line: 3,
  Pie: 4,
  Doughnut: 5,
  Scatter: 6,
  'Polar Area': 7,
  Radar: 8,
};
